import apiClient from './apiClient';
import { ENDPOINTS } from './apiConfig';
import { generateISLRXml } from '../utils/xmlGenerator';

/**
 * ------------------------------------------------------------------
 * SERVICIO DE RETENCIONES ISLR
 * ------------------------------------------------------------------
 */

const islrService = {
    /**
     * Obtener todas las retenciones ISLR (Resumen)
     * @param {Object} params - Parámetros opcionales
     * @param {string} params.empresa_rif - RIF de la empresa (Requerido)
     * @returns {Promise<Array>} Lista de retenciones ISLR
     */
    getAll: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams(params).toString();
            const url = `${ENDPOINTS.RETENCION_ISLR_SUMMARY}?${queryParams}`;
            const response = await apiClient.get(url);
            return response.data || response;
        } catch (error) {
            console.error('Error fetching retenciones ISLR:', error);
            throw error;
        }
    },

    /**
     * Crear cabecera de retención ISLR
     * POST /api/retencion/islr/header
     */
    createHeader: async (headerData) => {
        try {
            const response = await apiClient.post(ENDPOINTS.RETENCION_ISLR_HEADER, headerData);
            return response.data || response;
        } catch (error) {
            console.error('Error creating ISLR header:', error);
            throw error;
        }
    },

    /**
     * Actualizar cabecera de retención ISLR
     * PUT /api/retencion/islr/header/{id}
     * @param {number} id - ID de la cabecera
     * @param {Object} headerData - Datos de la cabecera a actualizar
     */
    updateHeader: async (id, headerData) => {
        try {
            const response = await apiClient.put(ENDPOINTS.RETENCION_ISLR_HEADER_UPDATE(id), headerData);
            return response.data || response;
        } catch (error) {
            console.error('Error updating ISLR header:', error);
            throw error;
        }
    },

    /**
     * Crear ítem de retención ISLR
     * POST /api/retencion/islr/item
     */
    createItem: async (itemData) => {
        try {
            // Asegurar tipos de datos correctos
            const payload = {
                ...itemData,
                base: parseFloat(itemData.base),
                porcentaje: parseFloat(itemData.porcentaje),
                retenido: parseFloat(itemData.retenido)
            };
            const response = await apiClient.post(ENDPOINTS.RETENCION_ISLR_ITEM, payload);
            return response.data || response;
        } catch (error) {
            console.error('Error creating ISLR item:', error);
            throw error;
        }
    },

    /**
    * Actualizar ítem de retención ISLR
    * POST /api/retencion/islr/item/update
    */
    updateItem: async (itemData) => {
        try {
            const payload = {
                ...itemData,
                base: parseFloat(itemData.base),
                porcentaje: parseFloat(itemData.porcentaje),
                retenido: parseFloat(itemData.retenido)
            };
            const response = await apiClient.post(ENDPOINTS.RETENCION_ISLR_ITEM_UPDATE, payload);
            return response.data || response;
        } catch (error) {
            console.error('Error updating ISLR item:', error);
            throw error;
        }
    },

    /**
     * Crear retención completa (1 cabecera + múltiples ítems) usando el endpoint /full
     * 
     * Este endpoint crea la cabecera y todos los items en una sola llamada.
     * 
     * @param {Object} retencionData - Datos completos de la retención
     * @param {Object} retencionData.header - Datos de la cabecera (sin items)
     * @param {Array} retencionData.items - Array de documentos
     * @returns {Promise<Object>} Retención creada con header e items
     */
    createRetencion: async (retencionData) => {
        try {
            const { header, items } = retencionData;

            if (!header) {
                throw new Error('Se requiere la información de la cabecera');
            }

            if (!items || items.length === 0) {
                throw new Error('Se requiere al menos un documento para crear la retención');
            }

            // Preparar el payload según el formato esperado por el endpoint /full
            // Todos los parámetros van en el body
            const payload = {
                empresa_rif: header.empresa_rif,
                proveedor_rif: header.proveedor_rif,
                fecha: header.fecha,
                periodo_fiscal: header.periodo_fiscal,
                numero_orden_pago: header.numero_orden_pago || '',
                items: items.map(item => ({
                    numero: item.numero,
                    numero_control: item.numero_control,
                    base: item.base,
                    porcentaje: parseFloat(item.porcentaje), // Asegurar tipo numérico
                    retenido: parseFloat(item.retenido),     // Asegurar tipo numérico
                    conceptos_idconceptos: item.conceptos_idconceptos
                }))
            };

            console.log('Creando retención ISLR completa con endpoint /full...', payload);

            const response = await apiClient.post(ENDPOINTS.RETENCION_ISLR_FULL, payload);

            const data = response.data || response;
            console.log('Retención ISLR creada exitosamente:', data);

            return {
                header: {
                    idcabezeras_ret: data.idcabezeras_ret || data.idcabezeras_islr || data.id,
                    id: data.idcabezeras_ret || data.idcabezeras_islr || data.id
                },
                success: true,
                totalItems: items.length,
                savedItems: items.length,
                failedItems: 0
            };
        } catch (error) {
            console.error('Error creating complete ISLR retencion:', error);
            throw error;
        }
    },

    /**
     * Anular retención ISLR (actualización simple)
     * Usa el endpoint PUT /api/retencion/islr/header/{id} para marcar como anulada
     * 
     * @param {number} headerId - ID de la cabecera a anular
     * @param {string} empresaRif - RIF de la empresa
     * @returns {Promise<Object>} Respuesta de la actualización
     */
    annulRetencion: async (headerId, empresaRif) => {
        try {
            console.log(`Anulando retención ISLR ${headerId}...`);

            // Obtener datos actuales del header para preservar otros campos
            const headerData = await islrService.getById(headerId, empresaRif);
            const firstItem = Array.isArray(headerData) ? headerData[0] : headerData;

            if (!firstItem) {
                throw new Error('Retención no encontrada');
            }

            // Preparar payload con todos los campos necesarios
            const updatePayload = {
                empresa_rif: empresaRif,
                proveedor_rif: firstItem.proveedor_rif,
                fecha: firstItem.fecha,
                periodo_fiscal: firstItem.periodo_fiscal,
                numero_orden_pago: firstItem.numero_orden_pago || '',
                anulada: 1  // ← Marcar como anulada
            };

            console.log('Actualizando header con anulada: 1', updatePayload);

            // Actualizar usando el endpoint PUT
            await islrService.updateHeader(headerId, updatePayload);

            console.log(`Retención ${headerId} anulada exitosamente`);

            return {
                success: true,
                id: headerId,
                message: 'Retención anulada exitosamente'
            };

        } catch (error) {
            console.error('Error anulando retención ISLR:', error);
            throw error;
        }
    },

    /**
     * Eliminar retención ISLR (Header)
     * @param {number} id - ID de la retención
     * @param {string} empresa_rif - RIF de la empresa (Requerido por seguridad)
     * @returns {Promise<Object>} Respuesta de eliminación
     */
    delete: async (id, empresa_rif) => {
        try {
            const queryParams = empresa_rif ? `?empresa_rif=${empresa_rif}` : '';
            const response = await apiClient.delete(`${ENDPOINTS.RETENCION_ISLR_HEADER_BY_ID(id)}${queryParams}`);
            return response.data || response;
        } catch (error) {
            console.error(`Error deleting retencion ISLR ${id}:`, error);
            throw error;
        }
    },

    /**
     * Obtener detalle de una retención ISLR
     * @param {number} id - ID de la retención
     * @param {string} empresa_rif - RIF de la empresa
     */
    getById: async (id, empresa_rif) => {
        try {
            const queryParams = empresa_rif ? `?empresa_rif=${empresa_rif}` : '';
            const response = await apiClient.get(`${ENDPOINTS.RETENCION_ISLR_DETAIL(id)}${queryParams}`);
            return response.data || response;
        } catch (error) {
            console.error(`Error fetching ISLR detail ${id}:`, error);
            throw error;
        }
    },

    /**
     * Eliminar un ítem individual de retención ISLR
     * DELETE /api/retencion/islr/item/{id}?empresa_rif={rif}
     * @param {number} id - ID del ítem a eliminar
     * @param {string} empresa_rif - RIF de la empresa (requerido)
     */
    deleteItem: async (id, empresa_rif) => {
        try {
            const queryParams = empresa_rif ? `?empresa_rif=${empresa_rif}` : '';
            const endpoint = `/retencion/islr/item/${id}${queryParams}`;
            const response = await apiClient.delete(endpoint);
            return response.data || response;
        } catch (error) {
            console.error(`Error deleting ISLR item ${id}:`, error);
            throw error;
        }
    },

    /**
     * Eliminar todos los ítems de una cabecera ISLR
     * DELETE /api/retencion/islr/items/{id}
     * @param {number} headerId - ID de la cabecera
     */
    deleteAllItems: async (headerId) => {
        try {
            const response = await apiClient.delete(ENDPOINTS.RETENCION_ISLR_ITEMS_DELETE(headerId));
            return response.data || response;
        } catch (error) {
            console.error(`Error deleting all ISLR items for header ${headerId}:`, error);
            throw error;
        }
    },

    /**
     * Verificar número de control
     * GET /api/retencion/islr/check-control
     */
    checkControlNumber: async (proveedorRif, numeroControl) => {
        try {
            const response = await apiClient.get(ENDPOINTS.RETENCION_ISLR_CHECK_CONTROL, {
                params: { proveedor_rif: proveedorRif, numero_control: numeroControl }
            });
            return response.data || response;
        } catch (error) {
            console.error('Error checking control number:', error);
            throw error;
        }
    },

    /**
     * Verificar número de factura
     * GET /api/retencion/islr/check-invoice
     */
    checkInvoiceNumber: async (proveedorRif, numero) => {
        try {
            const response = await apiClient.get(ENDPOINTS.RETENCION_ISLR_CHECK_INVOICE, {
                params: { proveedor_rif: proveedorRif, numero: numero }
            });
            return response.data || response;
        } catch (error) {
            console.error('Error checking invoice number:', error);
            throw error;
        }
    },

    /**
     * Obtener PDF de comprobante de retención ISLR
     * GET /api/retencion/islr/{id}/pdf
     */
    getPdf: async (id, empresa_rif) => {
        try {
            const queryParams = empresa_rif ? `?empresa_rif=${empresa_rif}` : '';
            const response = await apiClient.get(`${ENDPOINTS.RETENCION_ISLR_PDF(id)}${queryParams}`, {
                responseType: 'blob' // Importante para manejar archivos binarios
            });
            return response.data;
        } catch (error) {
            console.error(`Error download PDF for ISLR ${id}:`, error);
            throw error;
        }
    },

    /**
     * Descargar XML de Retenciones ISLR (Selección múltiple)
     * Generación Local
     */
    getXml: async (ids, empresa_rif) => {
        try {
            // 1. Obtener datos completos de las retenciones seleccionadas
            // Si el backend no tiene endpoint de batch detail, iteramos (no ideal pero funcional)
            // O mejor: usar getAll y filtrar (si están en memoria o paginados recientemente)
            // Para simplificar y asegurar datos: fetching details
            // NOTA: Para producción idealmente backend debería hacer esto.

            const promises = ids.map(id => islrService.getById(id, empresa_rif));
            const details = await Promise.all(promises);
            const flatRetenciones = details.flat(); // Aplanar arrays de detalles header+items

            // Necesitamos datos de empresa para el XML
            const empresa = { rif: empresa_rif };

            const xmlString = generateISLRXml(flatRetenciones, empresa);
            const blob = new Blob([xmlString], { type: 'application/xml' });
            return blob;
        } catch (error) {
            console.error('Error generating XML ISLR:', error);
            throw error;
        }
    },

    /**
     * Descargar XML de Retenciones ISLR por Rango de Fecha
     * Generación Local
     */
    getXmlByRange: async (startDate, endDate, empresa_rif) => {
        try {
            console.log(`Generando XML local para rango: ${startDate} - ${endDate}`);

            // 1. Consultar retenciones en el rango usando getAll
            // Asumimos que getAll soporta start_date y end_date o filtramos
            const params = {
                empresa_rif,
                fecha_inicio: startDate,
                fecha_fin: endDate
            };

            // Nota: getAll usa /summary. Si ese endpoint no trae detalles suficientes, 
            // el XML será básico. Ajustar según respuesta de getAll.
            const response = await islrService.getAll(params);
            const retenciones = Array.isArray(response) ? response : (response.data || []);

            if (retenciones.length === 0) {
                return null;
            }

            // Ocupamos datos de empresa
            const empresa = { rif: empresa_rif };

            const xmlString = generateISLRXml(retenciones, empresa);
            const blob = new Blob([xmlString], { type: 'application/xml' });
            return blob;

        } catch (error) {
            console.error('Error generating XML ISLR by range:', error);
            throw error;
        }
    }
};

export default islrService;
